import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, Loader2 } from "lucide-react";
import { ACCOUNT_CONFIRMATION_URL, RESET_PASSWORD_URL } from "@/lib/siteUrls";
import { trackEvent } from "@/lib/analytics";

type Mode = "signin" | "signup" | "forgot";

interface Props {
  onBack: () => void;
  /** Default tab when opening auth (e.g. from QuickSetup "Sign in"). */
  initialAuthMode?: "signin" | "signup";
}

export default function AuthPage({ onBack, initialAuthMode = "signup" }: Props) {
  const [mode, setMode] = useState<Mode>(initialAuthMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: RESET_PASSWORD_URL,
        });
        if (error) {
          const message = error.message.toLowerCase();
          const isRedirectMismatch =
            message.includes("redirect") && message.includes("not allowed");
          const isUnsupportedJwtAlg = message.includes("unsupported jwt algorithm es256");

          if (!isRedirectMismatch && !isUnsupportedJwtAlg) throw error;

          // Fallback to project's Site URL so reset email can still be sent.
          const fallback = await supabase.auth.resetPasswordForEmail(email);
          if (fallback.error) throw fallback.error;
        }
        toast.success("Check your email for a password reset link!");
        setMode("signin");
      } else if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: ACCOUNT_CONFIRMATION_URL },
        });
        if (error) throw error;
        // Supabase returns a user with no identities if the email already exists
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          toast.error("An account with this email already exists. Please sign in instead.");
          setMode("signin");
          return;
        }
        trackEvent("signup_completed");
        toast.success("Check your email to confirm your account!");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back!");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="flex-1 px-5 py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="font-display font-bold text-xl mb-1">
            {mode === "signup" ? "Create your account" : mode === "signin" ? "Welcome back" : "Reset password"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === "signup"
              ? "Start your 7-day free trial today"
              : mode === "signin"
              ? "Sign in to continue where you left off"
              : "Enter your email and we'll send you a reset link"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>

            {mode !== "forgot" && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-9"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            )}

            {mode === "signin" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-xs text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button type="submit" className="w-full h-11" disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "signup" ? (
                "Create Account"
              ) : mode === "signin" ? (
                "Sign In"
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            {mode === "forgot" ? (
              <button
                onClick={() => setMode("signin")}
                className="text-sm text-primary hover:underline"
              >
                Back to sign in
              </button>
            ) : (
              <button
                onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
                className="text-sm text-primary hover:underline"
              >
                {mode === "signup"
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Sign up"}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
